/**
 * Catalog Flipbook - Lazy-loading page flip viewer
 * Uses St Page Flip library (loaded from CDN)
 */
class CatalogFlipbook extends HTMLElement {
  constructor() {
    super();
    this.pageFlip = null;
    this.totalPages = 0;
    this.baseUrl = '';
    this.filePrefix = '';
    this.loadedPages = new Set();
    this.isFullscreen = false;
    this.bufferSize = 4; // pages to preload ahead/behind
  }

  connectedCallback() {
    this.totalPages = parseInt(this.dataset.totalPages) || 0;
    this.baseUrl = this.dataset.baseUrl || '';
    this.filePrefix = this.dataset.filePrefix || 'katalog-page-';

    // Ensure base URL ends with /
    if (this.baseUrl && !this.baseUrl.endsWith('/')) {
      this.baseUrl += '/';
    }

    if (!this.totalPages || !this.baseUrl) {
      console.warn('Catalog Flipbook: missing totalPages or baseUrl', { totalPages: this.totalPages, baseUrl: this.baseUrl });
      return;
    }

    this.loadLibrary().then(() => this.init()).catch(err => console.error('Catalog Flipbook: failed to load library', err));
  }

  async loadLibrary() {
    if (window.St && window.St.PageFlip) return;

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  init() {
    const wrapper = this.querySelector('.catalog-flipbook__wrapper');
    const bookEl = this.querySelector('.catalog-flipbook__book');

    if (!wrapper || !bookEl) return;

    // Calculate dimensions based on container
    const containerWidth = wrapper.clientWidth;
    const isMobile = window.innerWidth < 750;
    const pageWidth = isMobile ? Math.min(containerWidth - 20, 500) : Math.min(Math.floor(containerWidth / 2) - 10, 700);
    const pageHeight = Math.floor(pageWidth * 1.414); // A4 ratio

    // Create pages
    for (let i = 1; i <= this.totalPages; i++) {
      const page = document.createElement('div');
      page.className = 'catalog-flipbook__page catalog-flipbook__page--loading';
      page.dataset.pageNum = i;
      bookEl.appendChild(page);
    }

    // Initialize PageFlip
    this.pageFlip = new St.PageFlip(bookEl, {
      width: pageWidth,
      height: pageHeight,
      size: 'stretch',
      minWidth: 300,
      maxWidth: 900,
      minHeight: 420,
      maxHeight: 1300,
      showCover: true,
      mobileScrollSupport: true,
      swipeDistance: 30,
      clickEventForward: true,
      useMouseEvents: true,
      flippingTime: 800,
      startPage: 0,
      drawShadow: true,
      autoSize: true,
      maxShadowOpacity: 0.5
    });

    const pages = bookEl.querySelectorAll('.catalog-flipbook__page');
    this.pageFlip.loadFromHTML(pages);

    // Load initial pages
    this.loadVisiblePages();

    // Events
    this.pageFlip.on('flip', () => {
      this.loadVisiblePages();
      this.updatePageInfo();
    });

    this.setupControls();
    this.updatePageInfo();
  }

  getImageUrl(pageNum) {
    const paddedNum = String(pageNum).padStart(3, '0');
    return `${this.baseUrl}${this.filePrefix}${paddedNum}.jpg`;
  }

  loadPage(pageNum) {
    if (this.loadedPages.has(pageNum) || pageNum < 1 || pageNum > this.totalPages) return;

    const pageEl = this.querySelector(`.catalog-flipbook__page[data-page-num="${pageNum}"]`);
    if (!pageEl) return;

    this.loadedPages.add(pageNum);

    const img = new Image();
    img.onload = () => {
      pageEl.innerHTML = '';
      pageEl.appendChild(img);
      pageEl.classList.remove('catalog-flipbook__page--loading');
    };
    img.onerror = () => {
      pageEl.classList.remove('catalog-flipbook__page--loading');
      pageEl.innerHTML = '<span style="color:#999;font-size:14px">Page unavailable</span>';
    };
    img.src = this.getImageUrl(pageNum);
    img.alt = `Page ${pageNum}`;
  }

  loadVisiblePages() {
    if (!this.pageFlip) return;

    const current = this.pageFlip.getCurrentPageIndex();
    const start = Math.max(1, current - this.bufferSize);
    const end = Math.min(this.totalPages, current + this.bufferSize + 2);

    for (let i = start; i <= end; i++) {
      this.loadPage(i);
    }
  }

  setupControls() {
    const prevBtn = this.querySelector('[data-action="prev"]');
    const nextBtn = this.querySelector('[data-action="next"]');
    const firstBtn = this.querySelector('[data-action="first"]');
    const lastBtn = this.querySelector('[data-action="last"]');
    const fullscreenBtn = this.querySelector('[data-action="fullscreen"]');
    const downloadBtn = this.querySelector('[data-action="download"]');
    const pageInput = this.querySelector('.catalog-flipbook__page-input');

    prevBtn?.addEventListener('click', () => this.pageFlip.flipPrev());
    nextBtn?.addEventListener('click', () => this.pageFlip.flipNext());
    firstBtn?.addEventListener('click', () => this.pageFlip.flip(0));
    lastBtn?.addEventListener('click', () => this.pageFlip.flip(this.totalPages - 1));
    fullscreenBtn?.addEventListener('click', () => this.toggleFullscreen());
    downloadBtn?.addEventListener('click', () => this.download());

    pageInput?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        const page = parseInt(pageInput.value) - 1;
        if (page >= 0 && page < this.totalPages) {
          this.pageFlip.flip(page);
        }
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!this.isFullscreen && !this.closest(':focus-within')) return;
      if (e.key === 'ArrowLeft') this.pageFlip.flipPrev();
      if (e.key === 'ArrowRight') this.pageFlip.flipNext();
      if (e.key === 'Escape' && this.isFullscreen) this.toggleFullscreen();
    });

    // Listen for native fullscreen exit (e.g. pressing Escape)
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement && this.isFullscreen) {
        this.isFullscreen = false;
        const container = this.querySelector('.catalog-flipbook__container');
        container.classList.remove('catalog-flipbook--fullscreen');
        document.body.style.overflow = '';
        setTimeout(() => this.pageFlip?.update(), 300);
      }
    });
    document.addEventListener('webkitfullscreenchange', () => {
      if (!document.webkitFullscreenElement && this.isFullscreen) {
        this.isFullscreen = false;
        const container = this.querySelector('.catalog-flipbook__container');
        container.classList.remove('catalog-flipbook--fullscreen');
        document.body.style.overflow = '';
        setTimeout(() => this.pageFlip?.update(), 300);
      }
    });
  }

  updatePageInfo() {
    const current = this.pageFlip ? this.pageFlip.getCurrentPageIndex() + 1 : 1;
    const pageInput = this.querySelector('.catalog-flipbook__page-input');
    const totalSpan = this.querySelector('.catalog-flipbook__page-total');

    if (pageInput) pageInput.value = current;
    if (totalSpan) totalSpan.textContent = this.totalPages;
  }

  toggleFullscreen() {
    const container = this.querySelector('.catalog-flipbook__container');
    this.isFullscreen = !this.isFullscreen;

    if (this.isFullscreen) {
      // Try native fullscreen API first
      const el = container;
      if (el.requestFullscreen) {
        el.requestFullscreen();
      } else if (el.webkitRequestFullscreen) {
        el.webkitRequestFullscreen();
      } else {
        // Fallback to CSS fullscreen
        container.classList.add('catalog-flipbook--fullscreen');
        document.body.style.overflow = 'hidden';
      }
    } else {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      } else {
        container.classList.remove('catalog-flipbook--fullscreen');
        document.body.style.overflow = '';
      }
    }

    // Resize PageFlip to fit the new container
    setTimeout(() => {
      if (this.pageFlip) {
        const availableHeight = window.innerHeight - 80;
        const availableWidth = window.innerWidth;
        const isMobile = availableWidth < 750;
        const newPageHeight = Math.floor(availableHeight * 0.9);
        const newPageWidth = Math.floor(newPageHeight / 1.414);

        this.pageFlip.updateState({
          width: isMobile ? Math.min(availableWidth - 40, newPageWidth) : newPageWidth,
          height: newPageHeight
        });
        this.pageFlip.update();
      }
    }, 300);
  }

  download() {
    const url = this.dataset.downloadUrl;
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = '';
      a.click();
    }
  }
}

if (!customElements.get('catalog-flipbook')) {
  customElements.define('catalog-flipbook', CatalogFlipbook);
}
